import React, {
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import RowComponent from './row';
import ExpandRow from '../expand-row';
import treeRemap from './tree-remap';

const MAX_MARGIN = 22;

const TreeComponent = (props) => {
  const {
    expanded,
    isTester,
    lastChildAddedToEditMode,
    onAddChild,
    onChangeToEditModeLastChildAdded,
    onDragAndDrop,
    onEditChild,
    onElementPermutation,
    onRemoveChild,
    onSubmit,
    treeData,
    loading,
  } = props;

  // "treeData" is an array with all children of the tree.
  const treeDataRemaped = treeRemap(treeData);

  const [currentRowSelectedAccordion, setCurrentSelectedAccordion] = useState({});
  const [currentTesterButton, setCurrentTesterButton] = useState('');
  const [changeBackgroundClickRow, setChangeBackgroundClickRow] = useState('');
  const [isNotAutoExpandAllRows, setIsNotAutoExpandAllRows] = useState(false);
  const [currentNodeIdEditMode, setCurrentNodeIdEditMode] = useState(null);
  const [currentIdChildDrag, setCurrentIdChildDrag] = useState(null);
  const [rowDragHoverId, setRowDragHoverId] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState([]);

  const rootId = _.get(treeDataRemaped, 'id', '');
  const rootLabel = _.get(treeDataRemaped, 'label', '');
  const rootChild = _.get(treeDataRemaped, 'child', []);
  const rootProjectId = _.get(treeDataRemaped, 'projectId', '');

  const rootValues = {
    id: rootId,
    label: rootLabel,
    parentId: null,
    order: 1,
    projectId: rootProjectId,
  };

  /**
 * Used to let the tree have the behavior of an Accordion.
 * @param {func} handleRowClick Function to expand or collapse the row.
 * @param {string} rowId Current id of the row.
 * @param {number} level Level of the branch in the hierarchy of the tree.
 */
  const handleCurrentRowSelectedAccordion = useCallback((handleRowClick, rowId, level) => {
    const mapLevel = {
      [level]: {
        handleRowClick, rowId, level,
      },
    };

    const olderRowId = _.get(currentRowSelectedAccordion[level], 'rowId', '');
    const olderHandleRowClick = _.get(currentRowSelectedAccordion[level], 'handleRowClick', () => {});

    // Save the current row in the place of the older row that was expanded
    setCurrentSelectedAccordion((prev) => ({
      ...prev,
      ...mapLevel,
    }));

    // Collapse the older row that was expanded.
    if (currentRowSelectedAccordion[level] && olderRowId !== rowId) {
      olderHandleRowClick();
    }

    // Collapse current row if user click to collapse.
    if (currentRowSelectedAccordion[level] && olderRowId === rowId) {
      setCurrentSelectedAccordion((prev) => _.omitBy(prev, (v) => v.rowId === rowId));
    }

    // Collapse children if their level is greater than the current level.
    if (!_.isEmpty(currentRowSelectedAccordion)) {
      setCurrentSelectedAccordion((prev) => _.omitBy(prev, (v) => v.level > level));
    }

    // Collapse everything if user click on root node.
    if (rowId === rootId) {
      setCurrentSelectedAccordion({});
    }

    // Expand node.
    handleRowClick();

    // Used to reset the values when collapse the rows.
    setCurrentTesterButton('');
    setChangeBackgroundClickRow('');
  }, [currentRowSelectedAccordion, rootId]);

  /**
   * The first node selected can not be the Home node
   * We should filter out Home node clicks before saving
   * This function must be recursive (users can click many times on home node)
   */
  const buildPath = useCallback((path) => {
    let newPath = path;
    const firstNodeSelected = _.first(path) || {};

    // If the first node selected is the root node, remove it
    if (!_.isEmpty(firstNodeSelected) && firstNodeSelected.nodePathId === rootId) {
      newPath = _.tail(newPath);

      // update order
      newPath = _.map(newPath, (node, index) => ({
        ...node,
        order: index + 1,
      }));

      return buildPath(newPath);
    }

    return path;
  }, [rootId]);

  /**
   * Used to show the "I'd find it here" button in the last child.
   * @param {string} rowId Current row ID
   */
  const handleShowCurrentTesterButton = (rowId) => {
    setCurrentTesterButton(rowId);
  };

  /**
   * Used to change the background color of the clicked row without children.
   * @param {string} rowId Current row ID
   */
  const handleChangeBackgroundClickRow = (rowId) => {
    setChangeBackgroundClickRow(rowId);
  };

  /**
   * Used to not expand all the rows when user reach the page for the first time.
   */
  const handleCollapseAutoExpandedRow = () => {
    if (expanded && !isNotAutoExpandAllRows) {
      setIsNotAutoExpandAllRows(true);
    }
  };

  /**
   * Used to identify what node have entered in the edit mode.
   */
  const handleCurrentNodeIdEditMode = (rowId) => {
    setCurrentNodeIdEditMode(rowId);
  };

  /**
   * Used to know what is the current row that is being drag.
   * @param {string} currentChild
   */
  const handleCurrentIdChildDrag = (currentChild) => {
    setCurrentIdChildDrag(currentChild);
  };

  /**
   * Used to know what is the row where the user is hovering the dragged row.
   * @param {string} rowId
   */
  const handleRowDragHover = (rowId) => {
    setRowDragHoverId(rowId);
  };

  const handleClickNode = (node) => {
    if (isTester && !_.isEmpty(node)) {
      setSelectedNodes((prev) => {
        const newNode = { nodePathId: node.id, order: prev.length + 1 };
        return ([...prev, newNode]);
      });
    }
  };

  const handleTesterClickSubmit = useCallback((node) => {
    let newSelectedNodes = selectedNodes;
    const last = _.last(selectedNodes);

    if (!_.isEmpty(last) && last.nodePathId && last.nodePathId !== node.id) {
      const newNode = { nodePathId: node.id, order: selectedNodes.length + 1 };
      newSelectedNodes = [...selectedNodes, newNode];
    }

    newSelectedNodes = buildPath(newSelectedNodes);
    onSubmit(newSelectedNodes);
  }, [selectedNodes, buildPath, onSubmit]);

  /**
   * Used to recursive build the tree with your children.
   * @param {array} valueChild The array with all the chidren of the parent.
   * @param {number} marginLeft The size that the margin should go to the left.
   * @param {number} level The level where the current children are in the tree.
   * @returns An array with the component to show the rows of the current level in the tree.
   */
  const buildTree = (valueChild, marginLeft, level) => {
    if (_.isEmpty(valueChild)) { return null; }

    const subBranch = _.map(valueChild, (values, index) => {
      const {
        child, id,
      } = values;
      const isLastChild = (index === valueChild.length - 1);
      const isFirstChild = (index === 0);

      return (
        <ExpandRow key={id} expanded={expanded} noAutoExpand={isNotAutoExpandAllRows}>
          {/* Root child recursive */}
          <ExpandRow.RowTitle>
            <RowComponent
              changeBackgroundClickRow={changeBackgroundClickRow}
              childData={_.omit(values, ['child'])}
              currentIdChildDrag={currentIdChildDrag}
              currentNodeIdEditMode={currentNodeIdEditMode}
              currentRowSelectedAccordion={currentRowSelectedAccordion}
              currentTesterButton={currentTesterButton}
              handleChangeBackgroundClickRow={handleChangeBackgroundClickRow}
              handleCurrentRowSelectedAccordion={handleCurrentRowSelectedAccordion}
              handleShowCurrentTesterButton={handleShowCurrentTesterButton}
              hasChild={!_.isEmpty(child)}
              isFirstChild={isFirstChild}
              isLastChild={isLastChild}
              isTester={isTester}
              lastChildAddedToEditMode={lastChildAddedToEditMode}
              level={level}
              loading={loading}
              marginLeft={marginLeft}
              onAddChild={onAddChild}
              onChangeToEditModeLastChildAdded={onChangeToEditModeLastChildAdded}
              onClickNode={handleClickNode}
              onCollapseAutoExpandedRow={handleCollapseAutoExpandedRow}
              onCurrentIdChildDrag={handleCurrentIdChildDrag}
              onCurrentNodeIdEditMode={handleCurrentNodeIdEditMode}
              onDragAndDrop={onDragAndDrop}
              onEditChild={onEditChild}
              onElementPermutation={onElementPermutation}
              onRemoveChild={onRemoveChild}
              onRowDragHover={handleRowDragHover}
              onTesterClick={handleTesterClickSubmit}
              rowDragHoverId={rowDragHoverId}
            />
          </ExpandRow.RowTitle>

          <ExpandRow.RowContent>
            {/* Root child content recursive */}
            {!_.isEmpty(child) && buildTree(child, marginLeft + MAX_MARGIN, level + 1) }
          </ExpandRow.RowContent>

        </ExpandRow>
      );
    });

    return subBranch;
  };

  // If the array that was to have all the values of the tree is empty, return nothing.
  if (_.isEmpty(treeData)) return null;

  // Used to identify the level in the hierarchy of the tree.
  const level = 1;

  return (
    <>
      <ExpandRow
        expanded
      >
        {/* Root */}
        <ExpandRow.RowTitle>
          <RowComponent
            changeBackgroundClickRow={changeBackgroundClickRow}
            childData={rootValues}
            currentIdChildDrag={currentIdChildDrag}
            currentNodeIdEditMode={currentNodeIdEditMode}
            currentRowSelectedAccordion={currentRowSelectedAccordion}
            currentTesterButton={currentTesterButton}
            handleChangeBackgroundClickRow={handleChangeBackgroundClickRow}
            handleCurrentRowSelectedAccordion={handleCurrentRowSelectedAccordion}
            handleShowCurrentTesterButton={handleShowCurrentTesterButton}
            hasChild={!_.isEmpty(rootChild)}
            isRoot
            loading={loading}
            isTester={isTester}
            lastChildAddedToEditMode={lastChildAddedToEditMode}
            level={level}
            onAddChild={onAddChild}
            onChangeToEditModeLastChildAdded={onChangeToEditModeLastChildAdded}
            onClickNode={handleClickNode}
            onCollapseAutoExpandedRow={handleCollapseAutoExpandedRow}
            onCurrentIdChildDrag={handleCurrentIdChildDrag}
            onCurrentNodeIdEditMode={handleCurrentNodeIdEditMode}
            onDragAndDrop={onDragAndDrop}
            onEditChild={onEditChild}
            onElementPermutation={onElementPermutation}
            onRemoveChild={onRemoveChild}
            onRowDragHover={handleRowDragHover}
            onTesterClick={handleTesterClickSubmit}
            rowDragHoverId={rowDragHoverId}
          />
        </ExpandRow.RowTitle>

        <ExpandRow.RowContent>
          {/* Child content */}

          { _.map(rootChild, (values, index) => {
            const {
              child, id,
            } = values;
            const isLastChild = (index === rootChild.length - 1);
            const isFirstChild = (index === 0);

            return (
              <ExpandRow key={id} expanded={expanded} noAutoExpand={isNotAutoExpandAllRows}>

                <ExpandRow.RowTitle>
                  {/* Root Child */}
                  <RowComponent
                    changeBackgroundClickRow={changeBackgroundClickRow}
                    childData={_.omit(values, ['child'])}
                    currentIdChildDrag={currentIdChildDrag}
                    currentNodeIdEditMode={currentNodeIdEditMode}
                    currentRowSelectedAccordion={currentRowSelectedAccordion}
                    currentTesterButton={currentTesterButton}
                    handleChangeBackgroundClickRow={handleChangeBackgroundClickRow}
                    handleCurrentRowSelectedAccordion={handleCurrentRowSelectedAccordion}
                    handleShowCurrentTesterButton={handleShowCurrentTesterButton}
                    hasChild={!_.isEmpty(child)}
                    isFirstChild={isFirstChild}
                    isLastChild={isLastChild}
                    isTester={isTester}
                    lastChildAddedToEditMode={lastChildAddedToEditMode}
                    level={level + 1}
                    loading={loading}
                    marginLeft={MAX_MARGIN}
                    onAddChild={onAddChild}
                    onChangeToEditModeLastChildAdded={onChangeToEditModeLastChildAdded}
                    onClickNode={handleClickNode}
                    onCollapseAutoExpandedRow={handleCollapseAutoExpandedRow}
                    onCurrentIdChildDrag={handleCurrentIdChildDrag}
                    onCurrentNodeIdEditMode={handleCurrentNodeIdEditMode}
                    onDragAndDrop={onDragAndDrop}
                    onEditChild={onEditChild}
                    onElementPermutation={onElementPermutation}
                    onRemoveChild={onRemoveChild}
                    onRowDragHover={handleRowDragHover}
                    onTesterClick={handleTesterClickSubmit}
                    rowDragHoverId={rowDragHoverId}
                  />
                </ExpandRow.RowTitle>

                <ExpandRow.RowContent>
                  {/* Root Child content */}
                  {!_.isEmpty(child) && buildTree(child, MAX_MARGIN * 2, level + 2)}
                </ExpandRow.RowContent>

              </ExpandRow>

            );
          })}
        </ExpandRow.RowContent>

      </ExpandRow>

    </>
  );
};

TreeComponent.propTypes = {
  treeData: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    id: PropTypes.string,
    parentId: PropTypes.string,
    projectId: PropTypes.string,
  })).isRequired,
  expanded: PropTypes.bool,
  isTester: PropTypes.bool,
  lastChildAddedToEditMode: PropTypes.string,
  onAddChild: PropTypes.func,
  onChangeToEditModeLastChildAdded: PropTypes.func,
  onDragAndDrop: PropTypes.func,
  onEditChild: PropTypes.func,
  onElementPermutation: PropTypes.func,
  onRemoveChild: PropTypes.func,
  onSubmit: PropTypes.func,
  loading: PropTypes.bool,
};

TreeComponent.defaultProps = {
  expanded: false,
  isTester: false,
  lastChildAddedToEditMode: null,
  loading: false,
  onAddChild: () => {},
  onChangeToEditModeLastChildAdded: () => {},
  onDragAndDrop: () => {},
  onEditChild: () => {},
  onElementPermutation: () => {},
  onRemoveChild: () => {},
  onSubmit: () => {},
};

export default TreeComponent;
